/**
 * Style & Fit Advisor (sections/style-advisor.liquid) — Editorial-Luxus-Level
 * Guardrail (skills/fashion_web_design.md, Abschnitt 0): keine externen
 * API-Calls, keine App-Abhaengigkeiten, keine Beruehrung von DSers/Salestio-
 * Daten oder bestehenden Produkt-Tags. Nutzt ausschliesslich die native
 * Shopify Cart-AJAX-API (window.routes.cart_add_url, siehe layout/theme.liquid)
 * sowie Dawns bereits global geladene <modal-dialog>/<modal-opener>-Web-
 * Components (assets/global.js) fuer Open/Close/Focus-Trap - keine eigene
 * Modal-Logik. Staggered-Entrance-Animationen laufen rein ueber CSS
 * (@keyframes + animation-delay via --stagger-index), hier wird nur der
 * --stagger-index pro Kachel gesetzt.
 *
 * Motion-Engine (assets/motion-engine.js) liefert ab hier echte Spring-
 * Physik fuer die Fit-Gauge sowie Glow-/Press-Feedback auf den
 * Auswahlkarten - ueber window.whenMotionReady()/window.MotionEngine,
 * mit Fallback auf das bisherige, rein CSS-/Reflow-basierte Verhalten,
 * falls die Engine (noch) nicht bereitsteht.
 */

if (!customElements.get('style-advisor')) {
  customElements.define(
    'style-advisor',
    class StyleAdvisor extends HTMLElement {
      constructor() {
        super();

        this.steps = ['occasion', 'silhouette', 'fit', 'result'];
        this.currentStep = this.steps[0];
        this.state = { occasion: null, silhouette: null, fitResult: null };

        const dataScript = this.querySelector('[data-style-advisor-data]');
        this.data = dataScript ? JSON.parse(dataScript.textContent) : {};

        this.addEventListener('click', this.handleClick.bind(this));
        this.addEventListener('submit', this.handleFitSubmit.bind(this));

        window.whenMotionReady((engine) => {
          this.motion = engine;
          this.querySelectorAll('.style-advisor__option-card, .style-advisor__submit, .style-advisor__add-all').forEach(
            (el) => {
              engine.magneticGlow(el);
              engine.pressFeedback(el);
            }
          );
        });
      }

      handleClick(event) {
        const occasionCard = event.target.closest('[data-occasion-tag]');
        if (occasionCard) {
          this.state.occasion = occasionCard.getAttribute('data-occasion-tag');
          this.setPressed(occasionCard);
          this.goToStep('silhouette');
          return;
        }

        const silhouetteCard = event.target.closest('[data-silhouette-tag]');
        if (silhouetteCard) {
          this.state.silhouette = silhouetteCard.getAttribute('data-silhouette-tag');
          this.setPressed(silhouetteCard);
          this.goToStep('fit');
          return;
        }

        const backButton = event.target.closest('[data-step-back]');
        if (backButton) {
          this.goToStep(this.previousStep());
          return;
        }

        const addAllButton = event.target.closest('[data-add-all-to-cart]');
        if (addAllButton) this.addAllToCart(addAllButton);
      }

      setPressed(selectedCard) {
        const grid = selectedCard.closest('[data-option-grid]');
        if (!grid) return;
        grid.querySelectorAll('[aria-pressed]').forEach((card) => {
          card.setAttribute('aria-pressed', card === selectedCard ? 'true' : 'false');
        });
      }

      handleFitSubmit(event) {
        const form = event.target.closest('[data-fit-form]');
        if (!form) return;
        event.preventDefault();

        const usualSize = form.querySelector('[name="usual_size"]').value;
        this.state.fitResult = this.calculateFit(this.data.fitRating, usualSize);
        this.renderFitResult(this.state.fitResult);
        this.renderCuratedLook();
        this.goToStep('result');
      }

      /**
       * Bewusst einfache, transparente Platzhalter-Heuristik: verschiebt die
       * vom Kunden angegebene gewohnte Groesse um eine Stufe, abhaengig von
       * custom.fit_rating.fit des Produkts (runs_small -> eine Groesse
       * hoch, runs_large -> eine Groesse runter, true_to_size -> unveraendert).
       * Kein koerpermassbasiertes Modell - fachliche Verfeinerung der
       * eigentlichen Fit-Logik ist ein separater, spaeterer Schritt.
       */
      calculateFit(fitRating, usualSize) {
        const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
        const currentIndex = sizes.indexOf(usualSize);
        if (currentIndex === -1 || !fitRating) {
          return { recommended: usualSize, note: (fitRating && fitRating.note) || '' };
        }
        let recommendedIndex = currentIndex;
        if (fitRating.fit === 'runs_small') recommendedIndex = Math.min(currentIndex + 1, sizes.length - 1);
        if (fitRating.fit === 'runs_large') recommendedIndex = Math.max(currentIndex - 1, 0);
        return { recommended: sizes[recommendedIndex], note: fitRating.note || '' };
      }

      renderFitResult(fitResult) {
        const target = this.querySelector('[data-fit-result]');
        if (!target) return;
        target.textContent = fitResult.note
          ? `Empfehlung: ${fitResult.recommended} — ${fitResult.note}`
          : `Empfehlung: ${fitResult.recommended}`;
      }

      renderCuratedLook() {
        const grid = this.querySelector('[data-look-grid]');
        if (!grid) return;
        const products = this.data.completeTheLook || [];

        if (!products.length) {
          grid.innerHTML = '';
          return;
        }

        grid.innerHTML = products
          .map(
            (product, index) => `
              <label class="style-advisor__look-item" style="--stagger-index: ${index}">
                <input type="checkbox" data-look-variant-id="${product.variantId}" checked>
                <img src="${product.image}" alt="${product.title}" loading="lazy" width="400" height="500">
                <span class="style-advisor__look-item-toggle" aria-hidden="true">✓</span>
                <span class="style-advisor__look-item-info">
                  <span class="style-advisor__look-item-title">${product.title}</span>
                  <span class="style-advisor__look-item-price">${product.price}</span>
                </span>
              </label>
            `
          )
          .join('');

        if (this.motion) {
          grid.querySelectorAll('.style-advisor__look-item').forEach((item) => this.motion.magneticGlow(item));
        }
      }

      goToStep(stepName) {
        this.querySelectorAll('[data-step]').forEach((stepEl) => {
          stepEl.hidden = stepEl.getAttribute('data-step') !== stepName;
        });
        this.currentStep = stepName;

        if (stepName === 'fit') this.activateFitGauge();
      }

      /**
       * Re-triggert die Fit-Gauge-Slide-Animation JEDES Mal, wenn Step 3
       * (erneut) betreten wird - nicht nur beim ersten Mal. Der Remove/
       * Reflow/Add-Zyklus auf .is-active bleibt IMMER bestehen (steuert die
       * CSS-`left`-Transition der Positionierung, siehe style-advisor.css) -
       * eine reine CSS-Transition wuerde beim zweiten Betreten sonst nicht
       * erneut feuern (Klasse waere schon gesetzt, kein "Wechsel" fuer den
       * Browser erkennbar). Mit Motion-Engine kommt zusaetzlich ein echter
       * Spring-Glow-Puls auf den Indikator obendrauf (nur opacity/box-shadow,
       * bewusst NICHT transform - das Element nutzt bereits
       * transform: translate(-50%, -50%) zur Zentrierung, ein
       * ueberschreibender WAAPI-transform wuerde diese Zentrierung zerstoeren).
       */
      activateFitGauge() {
        const gauge = this.querySelector('[data-fit-gauge]');
        if (!gauge) return;

        gauge.classList.remove('is-active');
        void gauge.offsetWidth; // erzwingt Reflow
        requestAnimationFrame(() => {
          gauge.classList.add('is-active');
          if (this.motion && this.motion.lib === 'motion') {
            const indicator = gauge.querySelector('.style-advisor__fit-gauge-indicator');
            if (indicator) {
              this.motion.animate(
                indicator,
                { opacity: [0.3, 1], boxShadow: ['0 0 0 0 rgba(var(--style-advisor-metallic-1), 0.6)', '0 0 0 10px rgba(var(--style-advisor-metallic-1), 0)'] },
                { easing: this.motion.SPRING_SOFT, duration: 0.7 }
              );
            }
          }
        });
      }

      previousStep() {
        const index = this.steps.indexOf(this.currentStep);
        return this.steps[Math.max(index - 1, 0)];
      }

      /**
       * Native Shopify Cart-AJAX-API (window.routes.cart_add_url) - keine
       * externe App, kein Drittanbieter-SDK. sections/sections_url lassen
       * Shopify den bereits vorhandenen Cart-Drawer-Markup direkt mitliefern;
       * cart-drawer.js::renderContents() uebernimmt danach unveraendert die
       * Darstellung/das Oeffnen - derselbe Mechanismus wie beim regulaeren
       * Add-to-Cart-Formular (assets/product-form.js), keine parallele
       * Cart-Logik. data-state steuert das visuelle Feedback (Pulse waehrend
       * "loading", kurzer "success"-Zustand danach, siehe style-advisor.css).
       */
      addAllToCart(button) {
        const items = Array.from(this.querySelectorAll('[data-look-variant-id]:checked')).map((input) => ({
          id: input.getAttribute('data-look-variant-id'),
          quantity: 1,
        }));
        if (!items.length) return;

        const originalLabel = button.textContent;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-state', 'loading');
        button.textContent = 'Wird hinzugefügt …';

        fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items,
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Cart-Add fehlgeschlagen (${response.status})`);
            return response.json();
          })
          .then((parsedState) => {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(parsedState);
            }
            button.setAttribute('data-state', 'success');
            button.textContent = '✓ Outfit hinzugefügt';
            if (this.motion && this.motion.lib === 'motion') {
              this.motion.animate(button, { scale: [1, 1.05, 1] }, { easing: this.motion.SPRING_SNAPPY, duration: 0.5 });
            }
            const modal = this.closest('modal-dialog');
            setTimeout(() => {
              if (modal) modal.hide();
              button.removeAttribute('data-state');
              button.textContent = originalLabel;
            }, 900);
          })
          .catch((error) => {
            console.error('[style-advisor]', error);
            button.removeAttribute('data-state');
            button.textContent = originalLabel;
          })
          .finally(() => {
            button.removeAttribute('aria-disabled');
          });
      }
    }
  );
}
